import { DateUtils, HttpError, VerifyCustomerExistResponse } from '@eo-storefronts/eo-core'
import {
  AUTHENTICATION_FLOW_STATE,
  AuthenticationFlowState
} from '~/src/stores/authentication-flow'
import { useEoState, useSetEoState } from '~/src/hooks/useEoState'
import useVerifyCustomerExist from '~/src/hooks/auth/useVerifyCustomerExist'
import { useEffect } from 'react'
import { AuthenticationFlowDialogNameEnum } from '~/src/types/DialogNameEnum'
import { AUTHENTICATION_STATE, AuthenticationState } from '~/src/stores/authentication'
import useSocialSignIn from '~/src/hooks/auth/useSocialSignIn'
import { UseAuthenticationFlowDialogsReturnType } from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import { useSnackbar } from 'notistack'
import { useTranslations } from '~/src/hooks/useTranslations'

interface ReturnsType {
  onSubmit(): Promise<void>,
}

const useAuthenticationFlowLoginTypeSelectionOnSubmit = (
  {
    goToStep,
    end
  }: Pick<UseAuthenticationFlowDialogsReturnType, 'goToStep' | 'end'>
): ReturnsType => {
  const { t } = useTranslations()
  const setAuthState = useSetEoState<AuthenticationState>(AUTHENTICATION_STATE)
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { verify, errors, loading: isLoading } = useVerifyCustomerExist()
  const { login, loading: isLoadingSocialSignIn } = useSocialSignIn({ goToStep, end })
  const { enqueueSnackbar } = useSnackbar()
  
  const _handleCodeResponseType = () => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      confirmationCodeSentAt: DateUtils.addMinutes(3, new Date()).toISOString()
    }))

    goToStep(AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_CONFIRMATION_CODE)
  }

  const _handlePasswordResponseType = () => {
    goToStep(AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_LOGIN)
  }

  const _handleActivationResponseType = (verifyUserExistResponse: VerifyCustomerExistResponse | undefined) => {
    if (verifyUserExistResponse?.data?.id && verifyUserExistResponse?.data?.uid) {
      setAuthState((state: AuthenticationState) => ({
        ...state,
        customer: {
          ...state.customer,
          token: '',
          id: verifyUserExistResponse.data!.id,
          uid: verifyUserExistResponse.data!.uid
        }
      }))
    }

    if (verifyUserExistResponse?.data?.activationType === 'CODE') {
      return _handleCodeResponseType()
    }
    
    goToStep(AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_REGISTER_CONFIRMATION)
  }

  const _handleGoogleResponseType = () => {
    void login('google')
  }

  const _handleAppleResponseType = () => {
    void login('apple')
  }

  const _handleResponseType = (verifyUserExistResponse: VerifyCustomerExistResponse | undefined): void => {
    switch (verifyUserExistResponse?.type) {
      case 'CODE':
        _handleCodeResponseType()
        break
      case 'PASSWORD':
        _handlePasswordResponseType()
        break
      case 'ACTIVATION':
        _handleActivationResponseType(verifyUserExistResponse)
        break
      case 'GOOGLE':
        _handleGoogleResponseType()
        break
      case 'APPLE':
        _handleAppleResponseType()
        break
    }
  }

  const onSubmit = async (): Promise<void> => {
    try {
      const response: VerifyCustomerExistResponse | undefined = await verify({
        email: authenticationFlowState.customer?.email || null,
        phone: authenticationFlowState.customer?.phone || null
      })

      setAuthenticationFlowState((state: AuthenticationFlowState) => ({
        ...state,
        type: response?.type || null,
        loginTypeResponse: response || null
      }))
      _handleResponseType(response)
    } catch (e: any | HttpError) {
      if ((e instanceof HttpError) && e.httpStatus === 404) {
        goToStep(AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_REGISTER)

        return
      }

      enqueueSnackbar(t('errors.defaultError'), { variant: 'error' })
    }
  }

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      isLoading: isLoading || isLoadingSocialSignIn
    }))
  }, [ isLoading, isLoadingSocialSignIn ])

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      errors
    }))
  }, [ errors ])

  return {
    onSubmit
  }
}

export default useAuthenticationFlowLoginTypeSelectionOnSubmit
